import React from 'react'
import { useDebouncedValue } from '@mantine/hooks'
import { graphql } from '@/gql'
import type { OrgUsersQuery as OrgUsersQueryType } from '@/gql/graphql.ts'
import { useQuery } from '@apollo/client'
import { useFeature } from 'featurehub-react-sdk'
import { atom, useAtom } from 'jotai'

export const OrgUsersQuery = graphql(`
  query OrgUsers(
    $search: String
    $first: Int
    $after: String
    $before: String
  ) {
    me {
      id
      org {
        id
        users(search: $search, first: $first, after: $after, before: $before) {
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
          nodes {
            id
            firstName
            lastName
            email
            role
          }
        }
      }
    }
  }
`)

interface UseUsersHookResponse {
  users: NonNullable<OrgUsersQueryType['me']>['org']['users']['nodes']
  totalCount: number
  pageSize: number
  currentPage: number
  searchTerm: string
  setSearchTerm: (term: string) => void
  nextPage: () => void
  previousPage: () => void
}

const currentPageAtom = atom(0)
const nextTokenAtom = atom('')
const previousTokenAtom = atom('')
const searchTermAtom = atom('')
const pageSizeAtom = atom(0)

export const useUsers = (): UseUsersHookResponse => {
  const [currentPage, setCurrentPage] = useAtom(currentPageAtom)
  const [nextToken, setNextToken] = useAtom(nextTokenAtom)
  const [previousToken, setPreviousToken] = useAtom(previousTokenAtom)
  const [searchTerm, setSearchTerm] = useAtom(searchTermAtom)
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300)
  const [pageSize] = useAtom(pageSizeAtom)
  const shouldSupportAutocompletion = useFeature('ENABLE_ACCOUNTS')
  const { data } = useQuery(OrgUsersQuery, {
    variables: {
      ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      ...(pageSize && { first: pageSize }),
      ...(nextToken && { after: nextToken }),
      ...(previousToken && { before: previousToken }),
    },
    skip: !shouldSupportAutocompletion,
  })

  const users = data?.me?.org?.users?.nodes ?? []
  const pageInfo = data?.me?.org?.users?.pageInfo

  const totalCount = React.useMemo(() => users.length ?? 0, [users])
  const maxPages = React.useMemo(
    () => Math.ceil(totalCount / pageSize),
    [pageSize, totalCount],
  )

  const nextPage = () => {
    if (!pageInfo?.hasNextPage) {
      return
    }
    setPreviousToken('')
    setNextToken(pageInfo?.endCursor ?? '')
    setCurrentPage((page) => Math.min(maxPages, page + 1))
  }

  const previousPage = () => {
    if (!pageInfo?.hasPreviousPage) {
      return
    }
    setNextToken('')
    setPreviousToken(pageInfo?.startCursor ?? '')
    setCurrentPage((page) => Math.max(0, page - 1))
  }

  const search = React.useCallback(
    (term: string) => {
      setNextToken('')
      setPreviousToken('')
      setCurrentPage(0)
      setSearchTerm(term)
    },
    [setCurrentPage, setNextToken, setPreviousToken, setSearchTerm],
  )

  return {
    searchTerm: searchTerm,
    setSearchTerm: search,
    users: users,
    totalCount: totalCount ?? 0,
    pageSize,
    currentPage,
    nextPage,
    previousPage,
  }
}
